import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { HashLink } from "react-router-hash-link";
import { useTranslation } from "react-i18next";
import "../../styles/Header.css";
import { useAuth } from "../../context/AuthContext";

function Header() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, user, logout } = useAuth();
  const [dropdowns, setDropdowns] = useState({
    event: false,
    about: false,
    adminDashboard: false,
    adminManagement: false,
    adminOrganization: false,
    adminResults: false
  });
  const dropdownRef = useRef(null);

  const isActive = (path) => {
    if (path === "/") {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  const toggleMenu = () => {
    const navbar = document.getElementById("navbar");
    navbar.classList.toggle("header__nav--active");
  };

  const toggleDropdown = (dropdown) => {
    setDropdowns(prev => ({
      event: dropdown === "event" ? !prev.event : false,
      about: dropdown === "about" ? !prev.about : false,
      adminDashboard: dropdown === "adminDashboard" ? !prev.adminDashboard : false,
      adminManagement: dropdown === "adminManagement" ? !prev.adminManagement : false,
      adminOrganization: dropdown === "adminOrganization" ? !prev.adminOrganization : false,
      adminResults: dropdown === "adminResults" ? !prev.adminResults : false
    }));
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdowns({
          event: false,
          about: false,
          adminDashboard: false,
          adminManagement: false,
          adminOrganization: false,
          adminResults: false
        });
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const [darkMode, setDarkMode] = useState(false);

  useEffect(() => {
    const isDark = localStorage.getItem("darkMode") === "true";
    setDarkMode(isDark);
    document.body.classList.toggle("dark-mode", isDark);
  }, []);

  const toggleDarkMode = () => {
    const newDarkMode = !darkMode;
    setDarkMode(newDarkMode);
    localStorage.setItem("darkMode", newDarkMode);
    document.body.classList.toggle("dark-mode", newDarkMode);
  };

  const [lang, setLang] = useState("de");

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const currentLang = urlParams.get("lang") || "de";
    setLang(currentLang);
  }, []);

  const { i18n } = useTranslation();

  const changeLanguage = (lang) => {
    setLang(lang);
    const url = new URL(window.location);
    url.searchParams.set("lang", lang);
    window.history.pushState({}, "", url);
    i18n.changeLanguage(lang);
  };

  const scrollToTop = () => {
    window.scrollTo(0, 0);
    const navbar = document.getElementById("navbar");
    navbar.classList.remove("header__nav--active");
  };

  const handleDonationClick = (e) => {
    e.preventDefault();
    navigate('/sponsors#private-donation');
    setTimeout(() => {
      const element = document.getElementById('private-donation');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  const handleHashClick = () => {
    const navbar = document.getElementById("navbar");
    navbar.classList.remove("header__nav--active");
  };

  const isAdmin = location.pathname.startsWith("/admin");

  const handleLogout = () => {
    logout();
    navigate("/admin");
  };

  // Admin-Menüpunkte
  const adminMenu = [
    {
      type: "dropdown",
      label: t("admin.menu.dashboard", "Dashboard"),
      icon: "fa-tachometer-alt",
      isActive: isActive("/admin/dashboard") || isActive("/admin/tailwind-dashboard"),
      dropdownKey: "adminDashboard",
      open: dropdowns.adminDashboard,
      items: [
        {
          type: "link",
          to: "/admin/dashboard",
          icon: "fa-tachometer-alt",
          label: t("admin.menu.originalDashboard", "Original Dashboard"),
          isActive: isActive("/admin/dashboard"),
          onClick: scrollToTop
        },
        {
          type: "link",
          to: "/admin/tailwind-dashboard",
          icon: "fa-palette",
          label: t("admin.menu.tailwindDashboard", "Tailwind Dashboard"),
          isActive: isActive("/admin/tailwind-dashboard"),
          onClick: scrollToTop
        }
      ]
    },
    {
      type: "dropdown",
      label: t("admin.menu.management", "Verwaltung"),
      icon: "fa-cogs",
      isActive: isActive("/admin/participants") || isActive("/admin/nations") || isActive("/admin/categories"),
      dropdownKey: "adminManagement",
      open: dropdowns.adminManagement,
      items: [
        {
          type: "link",
          to: "/admin/participants",
          icon: "fa-users",
          label: t("admin.menu.participants", "Teilnehmer"),
          isActive: isActive("/admin/participants"),
          onClick: scrollToTop
        },
        {
          type: "link",
          to: "/admin/nations",
          icon: "fa-flag",
          label: t("admin.menu.nations", "Nationen"),
          isActive: isActive("/admin/nations"),
          onClick: scrollToTop
        },
        {
          type: "link",
          to: "/admin/categories",
          icon: "fa-list",
          label: t("admin.menu.categories", "Kategorien"),
          isActive: isActive("/admin/categories"),
          onClick: scrollToTop
        }
      ]
    },
    {
      type: "dropdown",
      label: t("admin.menu.organization", "Organisation"),
      icon: "fa-calendar-alt",
      isActive: isActive("/admin/groups") || isActive("/admin/schedule"),
      dropdownKey: "adminOrganization",
      open: dropdowns.adminOrganization,
      items: [
        {
          type: "link",
          to: "/admin/groups",
          icon: "fa-layer-group",
          label: t("admin.menu.groups", "Gruppen"),
          isActive: isActive("/admin/groups"),
          onClick: scrollToTop
        },
        {
          type: "link",
          to: "/admin/schedule",
          icon: "fa-calendar-alt",
          label: t("admin.menu.schedule", "Zeitplan"),
          isActive: isActive("/admin/schedule"),
          onClick: scrollToTop
        },
        {
          type: "link",
          to: "/admin/settings",
          icon: "fa-cog",
          label: t("admin.menu.settings", "Einstellungen"),
          isActive: isActive("/admin/settings"),
          onClick: scrollToTop
        }
      ]
    },
    {
      type: "dropdown",
      label: t("admin.menu.results", "Ergebnisse"),
      icon: "fa-clipboard-list",
      isActive: isActive("/admin/results") || isActive("/admin/medal-table") || isActive("/admin/mvp-table"),
      dropdownKey: "adminResults",
      open: dropdowns.adminResults,
      items: [
        {
          type: "link",
          to: "/admin/results",
          icon: "fa-clipboard-list",
          label: t("admin.menu.results", "Ergebnisse"),
          isActive: isActive("/admin/results"),
          onClick: scrollToTop
        },
        {
          type: "link",
          to: "/admin/medal-table",
          icon: "fa-medal",
          label: t("admin.menu.medalTable", "Medaillenspiegel"),
          isActive: isActive("/admin/medal-table"),
          onClick: scrollToTop
        },
        {
          type: "link",
          to: "/admin/mvp-table",
          icon: "fa-trophy",
          label: t("admin.menu.mvpTable", "MVP-Tabelle"),
          isActive: isActive("/admin/mvp-table"),
          onClick: scrollToTop
        }
      ]
    }
  ];

  // Hauptmenü für Nicht-Admin
  const mainMenu = [
    {
      type: "link",
      to: "/",
      icon: "fa-home",
      label: t("header.home"),
      isActive: isActive("/"),
      onClick: scrollToTop
    },
    {
      type: "dropdown",
      label: t("header.event"),
      icon: "fa-calendar-alt",
      isActive: isActive("/taolu") || location.hash === "#schedule" || location.hash === "#location",
      dropdownKey: "event",
      open: dropdowns.event,
      items: [
        {
          type: "link",
          to: "/taolu",
          icon: "fa-fist-raised",
          label: t("header.taolu"),
          isActive: location.pathname === "/taolu",
          onClick: scrollToTop
        },
        {
          type: "hashlink",
          to: "/#schedule",
          icon: "fa-calendar-alt",
          label: t("header.schedule"),
          isActive: location.hash === "#schedule",
          onClick: handleHashClick
        },
        {
          type: "hashlink",
          to: "/#location",
          icon: "fa-map-marker-alt",
          label: t("header.location"),
          isActive: location.hash === "#location",
          onClick: handleHashClick
        }
      ]
    },
    {
      type: "dropdown",
      label: t("header.about"),
      icon: "fa-info-circle",
      isActive: isActive("/chinwoo") || isActive("/sponsors"),
      dropdownKey: "about",
      open: dropdowns.about,
      items: [
        {
          type: "link",
          to: "/chinwoo",
          icon: "fa-dragon",
          label: t("header.chinwoo"),
          isActive: location.pathname === "/chinwoo",
          onClick: scrollToTop
        },
        {
          type: "link",
          to: "/sponsors",
          icon: "fa-handshake",
          label: t("header.sponsors"),
          isActive: location.pathname === "/sponsors",
          onClick: scrollToTop
        }
      ]
    },
    {
      type: "link",
      to: "/downloads",
      icon: "fa-download",
      label: t("downloads.title"),
      isActive: isActive("/downloads"),
      onClick: scrollToTop
    },
    {
      type: "hashlink",
      to: "/#contact",
      icon: "fa-envelope",
      label: t("header.contact"),
      isActive: location.hash === "#contact",
      onClick: handleHashClick
    }
  ];

  return (
    <header className="header">
      <div className="header__container">
        <Link to="/" onClick={scrollToTop} className="header__brand">
          <img
            src="/images/logo.png"
            alt="Kung Fu Weltmeisterschaft Logo"
            className="header__logo"
          />
          <span className="header__brand-text">BER26</span>
        </Link>

        <nav className="header__nav" id="navbar" ref={dropdownRef}>
          {isAdmin ? (
            <>
              {adminMenu.map((item, idx) => {
                if (item.type === "link") {
                  return (
                    <Link
                      key={item.to}
                      to={item.to}
                      onClick={item.onClick}
                      className={`header__nav-link ${item.isActive ? "header__nav-link--active" : ""}`}
                    >
                      <i className={`fas ${item.icon}`}></i>
                      <span>{item.label}</span>
                    </Link>
                  );
                }
                if (item.type === "dropdown") {
                  return (
                    <div className="header__dropdown" key={item.label}>
                      <button
                        className={`header__nav-link header__dropdown-button ${item.isActive ? "header__nav-link--active" : ""}`}
                        onClick={() => toggleDropdown(item.dropdownKey)}
                      >
                        <i className={`fas ${item.icon}`}></i>
                        <span>{item.label}</span>
                        <i className={`fas fa-chevron-${item.open ? "up" : "down"} header__dropdown-arrow`}></i>
                      </button>
                      <div className={`header__dropdown-content ${item.open ? "header__dropdown-content--active" : ""}`}>
                        {item.items.map((sub, subIdx) => {
                          if (sub.type === "link") {
                            return (
                              <Link
                                key={sub.to}
                                to={sub.to}
                                onClick={sub.onClick}
                                className={`header__dropdown-item ${sub.isActive ? "header__dropdown-item--active" : ""}`}
                              >
                                <i className={`fas ${sub.icon}`}></i>
                                <span>{sub.label}</span>
                              </Link>
                            );
                          }
                          return null;
                        })}
                      </div>
                    </div>
                  );
                }
                return null;
              })}
            </>
          ) : (
            <>
              {mainMenu.map((item, idx) => {
                if (item.type === "link") {
                  return (
                    <Link
                      key={item.to}
                      to={item.to}
                      onClick={item.onClick}
                      className={`header__nav-link ${item.isActive ? "header__nav-link--active" : ""}`}
                    >
                      <i className={`fas ${item.icon}`}></i>
                      <span>{item.label}</span>
                    </Link>
                  );
                }
                if (item.type === "hashlink") {
                  return (
                    <HashLink
                      key={item.to}
                      to={item.to}
                      onClick={item.onClick}
                      className={`header__nav-link ${item.isActive ? "header__nav-link--active" : ""}`}
                    >
                      <i className={`fas ${item.icon}`}></i>
                      <span>{item.label}</span>
                    </HashLink>
                  );
                }
                if (item.type === "dropdown") {
                  return (
                    <div className="header__dropdown" key={item.label}>
                      <button
                        className={`header__nav-link header__dropdown-button ${item.isActive ? "header__nav-link--active" : ""}`}
                        onClick={() => toggleDropdown(item.dropdownKey)}
                      >
                        <i className={`fas ${item.icon}`}></i>
                        <span>{item.label}</span>
                        <i className={`fas fa-chevron-${item.open ? "up" : "down"} header__dropdown-arrow`}></i>
                      </button>
                      <div className={`header__dropdown-content ${item.open ? "header__dropdown-content--active" : ""}`}>
                        {item.items.map((sub, subIdx) => {
                          if (sub.type === "link") {
                            return (
                              <Link
                                key={sub.to}
                                to={sub.to}
                                onClick={sub.onClick}
                                className={`header__dropdown-item ${sub.isActive ? "header__dropdown-item--active" : ""}`}
                              >
                                <i className={`fas ${sub.icon}`}></i>
                                <span>{sub.label}</span>
                              </Link>
                            );
                          }
                          if (sub.type === "hashlink") {
                            return (
                              <HashLink
                                key={sub.to}
                                to={sub.to}
                                onClick={sub.onClick}
                                className={`header__dropdown-item ${sub.isActive ? "header__dropdown-item--active" : ""}`}
                              >
                                <i className={`fas ${sub.icon}`}></i>
                                <span>{sub.label}</span>
                              </HashLink>
                            );
                          }
                          return null;
                        })}
                      </div>
                    </div>
                  );
                }
                return null;
              })}
            </>
          )}
        </nav>

        <div className="header__controls">
          {isAuthenticated ? (
            <button type="button" className="header__auth-button" onClick={handleLogout}>
              <i className="fas fa-user-shield"></i>
              <span className="header__auth-user">
                {user?.username || t("auth.user", "Nutzer")}
                <span className="header__auth-role">
                  {t(`roles.${user?.role}`, user?.role === "admin" ? "Admin" : "Punktschreiber")}
                </span>
              </span>
              <i className="fas fa-sign-out-alt header__auth-icon"></i>
            </button>
          ) : (
            <Link to="/admin" className="header__auth-button header__auth-button--link">
              <i className="fas fa-sign-in-alt"></i>
              <span>{t("login.title", "Login")}</span>
            </Link>
          )}
          <div className="header__languages">
            <div
              onClick={() => changeLanguage("de")}
              className="header__language-option"
              aria-label="Deutsch"
            >
              <img
                src="/images/flags/DE.png"
                alt="Deutsch"
                className={`header__language-flag ${
                  lang === "de" ? "header__language-flag--active" : ""
                }`}
              />
            </div>
            <div
              onClick={() => changeLanguage("en")}
              className="header__language-option"
              aria-label="English"
            >
              <img
                src="/images/flags/GB.png"
                alt="English"
                className={`header__language-flag ${
                  lang === "en" ? "header__language-flag--active" : ""
                }`}
              />
            </div>
            <div
              onClick={() => changeLanguage("zh")}
              className="header__language-option"
              aria-label="Chinese"
            >
              <img
                src="/images/flags/CN.png"
                alt="Chinese"
                className={`header__language-flag ${
                  lang === "zh" ? "header__language-flag--active" : ""
                }`}
              />
            </div>
          </div>
          <div className="header__theme-toggle" onClick={toggleDarkMode}>
            <div className="theme-toggle__track">
              <div
                className={`theme-toggle__thumb ${
                  darkMode ? "theme-toggle__thumb--active" : ""
                }`}
              >
                <i
                  className={`fas ${
                    darkMode ? "fa-moon" : "fa-sun"
                  } theme-toggle__icon`}
                ></i>
              </div>
            </div>
          </div>
          <div className="header__donation-button">
            <button className="generic__button" onClick={handleDonationClick}>
              <i className="fas fa-heart"></i>
              {t("donations.private.button")}
            </button>
          </div>
        </div>

        <div className="header__menu-toggle" onClick={toggleMenu}>
          <i className="fas fa-bars"></i>
        </div>
      </div>
    </header>
  );
}

export default Header;